/**
 * Shared PWA + branding helpers for Doserne Academy.
 *
 *   • `useDoserneManifest()` — while a /doserne page is mounted:
 *       - swaps `<link rel="manifest">` to /manifest-doserne.json
 *       - swaps favicon (16/32), apple-touch-icon and mask-icon to
 *         Doserne branded PNGs so both browser tabs and iOS home-screen
 *         installs show Doserne — never the AllStillas mark.
 *       - swaps Open Graph metadata (og:title / og:description /
 *         og:image / og:url) so link previews on Messenger/WhatsApp/
 *         iMessage/Slack/etc render as "Doserne Academy" with the
 *         green Doserne image (no AllStillas branding).
 *       - swaps `<meta name="theme-color">` + `apple-mobile-web-app-title`
 *         and the document `<title>`.
 *     All originals are restored on unmount so AllStillas pages stay
 *     unaffected.
 *
 *   • `useDeferredInstallPrompt()` — captures the browser's
 *     `beforeinstallprompt` event (Chrome/Edge/Android) and exposes
 *     a clean `install()` trigger + `canInstall` / `installed`
 *     signals. Detects an already-installed standalone launch.
 */
import { useCallback, useEffect, useState } from "react";

const DOSERNE_OG_IMAGE = "/icons/doserne/og-image.png";
const DOSERNE_TITLE = "Doserne Academy — Internt læringsportal";
const DOSERNE_DESC = "Doserne Academy — daglig plan, prosjektchat, HMS-kurs og progresjon for stillasarbeidere.";
const DOSERNE_THEME = "#007a3d";

// Helpers -------------------------------------------------------------------
function upsertMeta(selector, attrName, attrValue, contentValue, restoreMap) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
    created = true;
    restoreMap.push({ type: "remove", el });
  } else {
    restoreMap.push({ type: "revert", el, prev: el.getAttribute("content") });
  }
  el.setAttribute("content", contentValue);
  return el;
}

function updateLink(selector, hrefValue, restoreMap) {
  const el = document.head.querySelector(selector);
  if (!el) return;
  restoreMap.push({ type: "revert-attr", el, attr: "href", prev: el.getAttribute("href") });
  el.setAttribute("href", hrefValue);
}

export function useDoserneManifest() {
  useEffect(() => {
    const restore = [];

    // 1. Manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      const orig = manifestLink.getAttribute("href");
      if (orig !== "/manifest-doserne.json") {
        manifestLink.setAttribute("href", "/manifest-doserne.json");
        restore.push({ type: "revert-attr", el: manifestLink, attr: "href", prev: orig });
      }
    }

    // 2. Favicons + apple-touch-icon + mask-icon
    updateLink('link[rel="icon"][sizes="32x32"]', "/icons/doserne/favicon-32x32.png", restore);
    updateLink('link[rel="icon"][sizes="16x16"]', "/icons/doserne/favicon-16x16.png", restore);
    updateLink('link[rel="apple-touch-icon"]',   "/icons/doserne/apple-touch-icon.png", restore);
    updateLink('link[rel="mask-icon"]',           "/icons/doserne/icon-192x192.png", restore);
    // Some browsers pick apple-touch-startup-image for iOS PWA splash
    updateLink('link[rel="apple-touch-startup-image"]', "/icons/doserne/icon-512x512.png", restore);

    // 3. Open Graph — link preview cards on Messenger / WhatsApp / iMessage / Slack
    upsertMeta('meta[property="og:title"]',       'property', 'og:title',       DOSERNE_TITLE, restore);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', DOSERNE_DESC,  restore);
    upsertMeta('meta[property="og:image"]',       'property', 'og:image',       new URL(DOSERNE_OG_IMAGE, window.location.origin).toString(), restore);
    upsertMeta('meta[property="og:type"]',        'property', 'og:type',        'website', restore);
    upsertMeta('meta[property="og:site_name"]',   'property', 'og:site_name',   'Doserne Academy', restore);
    upsertMeta('meta[property="og:url"]',         'property', 'og:url',         window.location.href, restore);
    // Twitter card
    upsertMeta('meta[name="twitter:card"]',        'name', 'twitter:card',        'summary_large_image', restore);
    upsertMeta('meta[name="twitter:title"]',       'name', 'twitter:title',       DOSERNE_TITLE, restore);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', DOSERNE_DESC,  restore);
    upsertMeta('meta[name="twitter:image"]',       'name', 'twitter:image',       new URL(DOSERNE_OG_IMAGE, window.location.origin).toString(), restore);

    // 4. Theme + PWA app names
    upsertMeta('meta[name="theme-color"]',                  'name', 'theme-color',                  DOSERNE_THEME, restore);
    upsertMeta('meta[name="apple-mobile-web-app-title"]',   'name', 'apple-mobile-web-app-title',   'Doserne Academy', restore);
    upsertMeta('meta[name="application-name"]',             'name', 'application-name',             'Doserne Academy', restore);
    upsertMeta('meta[name="description"]',                  'name', 'description',                  DOSERNE_DESC, restore);

    // 5. Document title (restored on unmount)
    const originalTitle = document.title;
    if (!/Doserne/i.test(originalTitle)) {
      document.title = "Doserne Academy";
      restore.push({ type: "title", prev: originalTitle });
    }

    return () => {
      for (const r of restore.reverse()) {
        if (r.type === "remove") r.el.remove();
        else if (r.type === "revert") r.el.setAttribute("content", r.prev || "");
        else if (r.type === "revert-attr") r.el.setAttribute(r.attr, r.prev || "");
        else if (r.type === "title") document.title = r.prev;
      }
    };
  }, []);
}

export function useDeferredInstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => {
    try {
      if (window.matchMedia("(display-mode: standalone)").matches) return true;
      if (window.navigator.standalone === true) return true;
    } catch { /* ignore */ }
    return false;
  });

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return false;
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      setPrompt(null);
      return choice?.outcome === "accepted";
    } catch { return false; }
  }, [prompt]);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return { canInstall: !!prompt && !installed, installed, install, isIOS };
}
